import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { ModelCatalog, Part } from "../catalog/schema";
import { proceduralBuilders } from "./procedural-emma-1";

export type LoadedModel = {
  root: THREE.Group;
  /** Orderable meshes (catalog match), keyed by meshName. */
  parts: Map<string, { part: Part; meshes: THREE.Mesh[] }>;
  /** Every Mesh in load order, including non-orderable ones. */
  allMeshes: THREE.Mesh[];
  /** Bounding sphere of the assembled model (radius in scene units). */
  sceneRadius: number;
  /** World-space center of the model. */
  sceneCenter: THREE.Vector3;
};

/**
 * Load the model described by the catalog (procedural or GLB), normalize its
 * scale and position, and resolve catalog parts to meshes.
 */
export async function loadModel(catalog: ModelCatalog): Promise<LoadedModel> {
  let root: THREE.Group;

  if (catalog.source.kind === "procedural") {
    const builder = proceduralBuilders[catalog.source.builder];
    if (!builder) {
      throw new Error(`Unknown procedural builder: ${catalog.source.builder}`);
    }
    root = builder(catalog);
  } else {
    const gltf = await new GLTFLoader().loadAsync(catalog.source.path);
    root = gltf.scene;
    root.traverse((n) => {
      const m = n as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
  }

  // Normalize scale & center the model so its base sits on Y=0.
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const targetMax = 2.0; // metres — comfortable framing
  const scale = targetMax / maxDim;

  root.scale.setScalar(scale);
  root.updateMatrixWorld(true);

  const box2 = new THREE.Box3().setFromObject(root);
  const center2 = box2.getCenter(new THREE.Vector3());
  root.position.x -= center2.x;
  root.position.z -= center2.z;
  root.position.y -= box2.min.y; // sit on floor
  root.updateMatrixWorld(true);

  // Recompute bounding sphere
  const finalBox = new THREE.Box3().setFromObject(root);
  const finalCenter = finalBox.getCenter(new THREE.Vector3());
  const finalSize = finalBox.getSize(new THREE.Vector3());
  const sceneRadius = Math.max(0.5, finalSize.length() / 2);

  // Collect meshes
  const allMeshes: THREE.Mesh[] = [];
  root.traverse((n) => {
    if ((n as THREE.Mesh).isMesh) allMeshes.push(n as THREE.Mesh);
  });

  // Build catalog → mesh map, bucketing by group name (since some catalog
  // entries correspond to a Group containing multiple primitives, like Lattenrost).
  const parts = new Map<string, { part: Part; meshes: THREE.Mesh[] }>();
  const collectByGroupName = (groupName: string): THREE.Mesh[] => {
    const collected: THREE.Mesh[] = [];
    root.traverse((n) => {
      if (n.name === groupName) {
        n.traverse((c) => {
          if ((c as THREE.Mesh).isMesh) collected.push(c as THREE.Mesh);
        });
      }
    });
    return collected;
  };

  const orphanMeshes = new Set(allMeshes);

  for (const part of catalog.parts) {
    if (part.hidden) continue;
    // Direct mesh-name match first
    const direct = allMeshes.filter((m) => m.name === part.meshName);
    let bucket = direct;
    if (bucket.length === 0) {
      // Group-name match (proceduralEmma1 does this for lattenrost/gitter/etc.)
      bucket = collectByGroupName(part.meshName);
    }
    if (bucket.length === 0) {
      console.warn(
        `[catalog] Part "${part.meshName}" (${part.artNr}) — no matching mesh in model.`,
      );
      continue;
    }
    parts.set(part.meshName, { part, meshes: bucket });
    bucket.forEach((m) => orphanMeshes.delete(m));
  }

  if (orphanMeshes.size > 0) {
    const names = Array.from(orphanMeshes)
      .map((m) => m.name || "(unnamed)")
      .slice(0, 12);
    console.warn(
      `[catalog] ${orphanMeshes.size} mesh(es) without catalog entry — non-orderable. First few:`,
      names,
    );
  }

  return { root, parts, allMeshes, sceneRadius, sceneCenter: finalCenter };
}
